import { useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardSidebar, { DashSection } from './components/DashboardSidebar';
import OverviewContent from './components/OverviewContent';
import MyDealsContent from './components/MyDealsContent';
import TaxRecordsContent from './components/TaxRecordsContent';
import { mockMember } from '../../mocks/dashboard';

/*
 * ── AUTH NOTE ──────────────────────────────────────────────────────────────
 * `mockMember` is a placeholder. Once Supabase is connected, replace with:
 *   const { data: { user } } = await supabase.auth.getUser()
 *   const { data: profile } = await supabase
 *     .from('profiles').select('*').eq('id', user.id).single()
 * ──────────────────────────────────────────────────────────────────────────
 */

const SECTION_TITLES: Record<DashSection, string> = {
  overview:    'Dashboard Overview',
  deals:       'My Deals',
  'tax-records': 'Tax Records',
};

export default function Dashboard() {
  const [activeSection, setActiveSection] = useState<DashSection>('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const member = mockMember; // ← swap for Supabase profile data when connected

  return (
    <div className="h-screen flex bg-[#111827] font-sans overflow-hidden">

      {/* ── Mobile overlay ── */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <div className={`
        fixed inset-y-0 left-0 z-50 lg:relative lg:z-auto
        transition-transform duration-300
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <DashboardSidebar
          active={activeSection}
          onSelect={(s) => { setActiveSection(s); setMobileMenuOpen(false); }}
          member={member}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
        />
      </div>

      {/* ── Main area ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Top bar */}
        <header className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-white/5 bg-[#111827]">
          <div className="flex items-center gap-4">
            {/* Mobile hamburger */}
            <button
              className="lg:hidden w-8 h-8 flex items-center justify-center text-white/50 hover:text-white cursor-pointer"
              onClick={() => setMobileMenuOpen(true)}
            >
              <i className="ri-menu-line text-xl" />
            </button>

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-white/30 hover:text-white/60 transition-colors whitespace-nowrap">
                Foreclosure Recovery
              </Link>
              <i className="ri-arrow-right-s-line text-white/20" />
              <span className="text-white/70 font-medium whitespace-nowrap">
                {SECTION_TITLES[activeSection]}
              </span>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {/* Notification bell */}
            <button className="relative w-9 h-9 flex items-center justify-center rounded-xl bg-white/5 text-white/50 hover:text-white hover:bg-white/8 transition-colors cursor-pointer">
              <i className="ri-notification-3-line text-base" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
            </button>

            {/* New listing alert */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-xl">
              <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse flex-shrink-0" />
              <span className="text-gold text-xs font-semibold whitespace-nowrap">3 New Alerts</span>
            </div>

            {/* Avatar */}
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold/20 border border-gold/30 text-gold text-sm font-bold flex-shrink-0 cursor-pointer">
              {member.avatarInitials}
            </div>
          </div>
        </header>

        {/* ── Auth notice banner (remove when Supabase connected) ── */}
        <div className="flex-shrink-0 flex items-center gap-3 px-6 py-3 bg-amber-500/10 border-b border-amber-500/20">
          <i className="ri-information-line text-amber-400 text-base flex-shrink-0" />
          <p className="text-amber-300/80 text-xs leading-relaxed">
            <strong className="text-amber-300">Preview mode:</strong> Showing mock data for {member.firstName} {member.lastName}. Connect Supabase to enable real authentication and live member data.
          </p>
          <Link to="/contact" className="text-amber-400 text-xs font-semibold whitespace-nowrap hover:text-amber-300 transition-colors ml-auto flex-shrink-0">
            Get Help →
          </Link>
        </div>

        {/* ── Scrollable content ── */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          {activeSection === 'overview'     && <OverviewContent member={member} />}
          {activeSection === 'deals'        && <MyDealsContent />}
          {activeSection === 'tax-records'  && <TaxRecordsContent />}
        </main>
      </div>
    </div>
  );
}
